import type { classDataProps } from "../types/common";

// 批次上傳精靈「步驟 1 建立班級」的純邏輯(kanban 106957 R1)。
// 建班 modal 的樣式與 API 呼叫沿用 BookPreview「派出作文」既有作法,
// 這裡只抽出可測的規則:開通碼何時必填、重名擋下、回傳正規化、清單更新。

// 書籍 isNeedInvition === "Y" 時,建班需輸入業務發放的開通碼(作為班級 _id)。
// 書單裡找不到該書(如 getBooks 失敗 fail-open 成空陣列)時當一般書處理,
// 不鎖死建立入口;若真是開通碼書,送出時由後端擋下、走 B2 錯誤提示流程。
export const bookNeedsActivation = (
  books: ReadonlyArray<{ _id: string; isNeedInvition?: "Y" | "N" }>,
  bookId: string
): boolean => books.find((b) => b._id === bookId)?.isNeedInvition === "Y";

// 建班表單可否送出:班名必填;開通碼書再加開通碼必填(全空白視同沒填)
export const canSubmitNewClass = (
  needActivation: boolean,
  className: string,
  activationCode: string
): boolean =>
  className.trim() !== "" && (!needActivation || activationCode.trim() !== "");

// 同書同名班級擋下(code review 106957)。比對規則:trim 後全等。
//
// 原始理由是「batch 以 className 找班,同名會共用容器、批改中互相污染」,該理由自
// 2026-08-24 起已不成立 —— 容器唯一性改為 (creatorID, bookID, classCode),不看班名
// (class_service.create_class)。保留這個檢查的理由換成:同一本書內兩個同名班,老師
// 在選班清單上分不出哪個是哪個。跨書同名則無妨,所以比對範圍必須限在本書
// (呼叫端傳 classesForUpload 的結果,不是整份清單)。
//
// 後端仍然不查班名重複,這裡擋不住的路徑(如改名)後端也不會擋。
export const isDuplicateClassName = (
  existing: ReadonlyArray<{ className: string }>,
  className: string
): boolean => {
  const target = className.trim();
  return existing.some((c) => c.className.trim() === target);
};

// createBookClass 的必填送出參數。注意:這**不是**實際邀請碼 ——
// 後端 invitionteacher.py:212-222 會丟棄前端送的 classCode、自產 8 碼數字,
// 實際邀請碼一律以後端回傳為準(見 normalizeCreatedClass)。
export const generateClassCode = (): string =>
  `${Date.now().toString(36)}${Math.random()
    .toString(36)
    .substring(2, 6)}`.toUpperCase();

// 建立成功後,把後端回傳正規化成 classDataProps:
// - classCode 一律採後端回傳(後端自產的才是真邀請碼);缺 classCode 回 null,
//   呼叫端必須走建立失敗流程 —— 顯示前端自產碼會讓老師抄到一組學生加不進來的碼
// - bookID 缺時以送出值補:少了它過不了精靈的 classesForUpload 過濾(嚴格比對
//   cls.bookID === bookId),會「建完看不到」
export const normalizeCreatedClass = (
  raw: Partial<classDataProps> | null | undefined,
  submitted: { bookID: string; className: string }
): classDataProps | null => {
  const classCode = raw?.classCode;
  if (!classCode) return null;
  return {
    ...raw,
    _id: raw?._id || `local-${classCode}`,
    bookID: raw?.bookID || submitted.bookID,
    className: raw?.className || submitted.className,
    classCode,
  };
};

// 建立成功後:append 到清單尾端並回傳應選取的班級(不重打清單 API,仿國中)
export const appendCreatedClass = (
  classes: readonly classDataProps[],
  created: classDataProps
): { classes: classDataProps[]; selected: classDataProps } => ({
  classes: [...classes, created],
  selected: created,
});

// 從 axios 錯誤取後端回應的 detail(FastAPI 錯誤格式)。
// 只認非空字串;validation error 的 detail 是陣列、直接餵給老師看不懂,回 null
// 讓呼叫端 fallback 通用文案。
export const extractApiErrorDetail = (error: unknown): string | null => {
  const detail = (
    error as { response?: { data?: { detail?: unknown } } } | null | undefined
  )?.response?.data?.detail;
  return typeof detail === "string" && detail.trim() !== "" ? detail : null;
};
